import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { PlatformAdapter } from '@rudi/api/platform';
import { createAuthClient, type AuthClient } from './provider';
import type { AuthUser, AuthStateInternal } from './types';

/** Public auth context shape exposed via useAuth() */
export interface AuthContextType {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
  /** PlatformAdapter for token storage and URL resolution */
  adapter: PlatformAdapter;
  /**
   * Optional: pre-loaded initial state (e.g. from localStorage sync read).
   * If provided, the provider skips initial loading and trusts this state.
   */
  initialState?: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
  } | null;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, adapter, initialState }) => {
  const clientRef = useRef<AuthClient | null>(null);

  // Create auth client once
  if (!clientRef.current) {
    clientRef.current = createAuthClient(adapter);
  }
  const authClient = clientRef.current;

  const [state, setState] = useState<AuthStateInternal>(() => {
    if (initialState) {
      return {
        user: initialState.user,
        accessToken: initialState.accessToken,
        refreshToken: initialState.refreshToken,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      };
    }
    return {
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,
    };
  });

  // Restore adapter-backed sessions on platforms where tokens are async
  // (for example, Expo SecureStore) and verify synchronous initial state.
  useEffect(() => {
    let cancelled = false;

    const clearSession = async () => {
      await adapter.setAccessToken(null);
      await adapter.setRefreshToken(null);
      if (!cancelled) {
        setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
        });
      }
    };

    if (initialState) {
      authClient.verifyToken().catch(() => {
        void clearSession();
      });
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const [storedAccessToken, storedRefreshToken] = await Promise.all([
          adapter.getAccessToken(),
          adapter.getRefreshToken(),
        ]);

        if (!storedAccessToken && !storedRefreshToken) {
          if (!cancelled) {
            setState(prev => ({ ...prev, isLoading: false }));
          }
          return;
        }

        let accessToken = storedAccessToken;
        let refreshToken = storedRefreshToken;

        if (storedRefreshToken) {
          accessToken = await authClient.refresh();
          refreshToken = await adapter.getRefreshToken();
        }

        if (!accessToken) {
          throw new Error('No access token available');
        }

        const user = await authClient.refreshUser();
        if (cancelled) return;

        setState({
          user,
          accessToken,
          refreshToken,
          isLoading: false,
          isAuthenticated: true,
          error: null,
        });
      } catch {
        await clearSession();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adapter, authClient, initialState]);

  const login = useCallback(
    async (username: string, password: string): Promise<void> => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await authClient.login(username, password);
        setState({
          user: result.user,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          isLoading: false,
          isAuthenticated: true,
          error: null,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Login failed';
        console.error('[Auth] Login error:', errorMessage);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        throw error;
      }
    },
    [authClient]
  );

  const logout = useCallback(async (): Promise<void> => {
    await authClient.logout();
    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    });
  }, [authClient]);

  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      const newToken = await authClient.refresh();
      if (newToken) {
        const newState = authClient.getState();
        setState(prev => ({
          ...prev,
          accessToken: newState.accessToken,
          refreshToken: newState.refreshToken,
        }));
      } else {
        // Refresh failed, logout was called internally
        setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isLoading: false,
          isAuthenticated: false,
          error: null,
        });
      }
      return newToken;
    } catch {
      await logout();
      return null;
    }
  }, [authClient, logout]);

  const refreshUser = useCallback(async (): Promise<void> => {
    const user = await authClient.refreshUser();
    setState(prev => ({ ...prev, user }));
  }, [authClient]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const value = useMemo(
    (): AuthContextType => ({
      ...state,
      login,
      logout,
      refresh,
      refreshUser,
      clearError,
    }),
    [state, login, logout, refresh, refreshUser, clearError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth state and actions.
 * Must be used within an <AuthProvider>.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
